import {
  COLUMN_BASED_CONFIG,
  DEFAULT_WATCH_TOPIC_SCHEME,
  ROW_BASED_CONFIG,
  WATCH_COMPONENT_IDS,
  WATCH_REPLY_ROOMS,
  WATCH_ROOMS,
  type WatchCommand,
  type WatchConfigTableName,
  type WatchRoom,
  type WatchTopicScheme,
} from './watchSocket.constants';
import type {
  WatchConfigAnnouncementPayload,
  WatchConfigOptions,
  WatchEnvelope,
  WatchFeedbackPayload,
  WatchInboundMessage,
  WatchInboundMessageByRoom,
  WatchOutboundMessage,
  WatchPayload,
} from './watchSocket.types';
import { getConfigTableIdColumn, isWatchConfigTable } from './watchSocketConfigTables';
import { buildCommandCorrelationKey, buildConfigCorrelationKey } from './watchSocketCorrelation';

interface CommandMessageParams {
  unitId: string;
  command: WatchCommand;
  requestId: string;
  args?: unknown;
  topicScheme?: WatchTopicScheme;
}

interface ConfigMessageParams {
  unitId: string;
  table: WatchConfigTableName;
  topicScheme?: WatchTopicScheme;
}

interface ChangeConfigMessageParams extends ConfigMessageParams {
  options: WatchConfigOptions;
}

const isPlainObject = (value: unknown): value is WatchPayload =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isWatchRoom = (value: unknown): value is WatchRoom =>
  typeof value === 'string' && (WATCH_ROOMS as readonly string[]).includes(value);

export const isReplyRoom = (room: WatchRoom): boolean => (WATCH_REPLY_ROOMS as readonly string[]).includes(room);

type ComponentDataRoom = 'components/input_data' | 'components/output_data';
type ComponentDataMessage = WatchInboundMessageByRoom[ComponentDataRoom];

export const isComponentDataRoom = (room: WatchRoom): room is ComponentDataRoom =>
  room === 'components/input_data' || room === 'components/output_data';

export const isComponentDataMessage = (message: WatchInboundMessage): message is ComponentDataMessage =>
  isComponentDataRoom(message.room);

// A room only keeps its latest message, so carry forward the latest payload per component
export const accumulateComponentMessages = <T extends ComponentDataMessage>({
  previous,
  message,
}: {
  previous: T | undefined;
  message: T;
}): T => ({
  ...message,
  payload: message.payload.fields ? message.payload : (previous?.payload ?? message.payload),
  components: { ...previous?.components, ...message.components },
});

// A room only keeps its latest message, so carry forward the latest payload per asset
export const accumulateAssetMessages = ({
  previous,
  message,
}: {
  previous: WatchInboundMessage | undefined;
  message: Extract<WatchInboundMessage, { room: 'assets/state' | 'assets/timeseries' }>;
}) => {
  const previousAssets = (previous as typeof message | undefined)?.assets;
  const incomingAssets = message.assets;
  const { asset_type: assetType, asset_id: assetId } = message.payload.tags ?? {};

  if (assetId === undefined) {
    return { ...message, assets: { ...previousAssets, ...incomingAssets } };
  }

  return {
    ...message,
    assets: {
      ...previousAssets,
      ...incomingAssets,
      [`${assetType}:${assetId}`]: message.payload,
    },
  };
};

/**
 * Full topic for a room/suffix - e.g. ``buildTopic('battery', 'GREENER_001', 'state')`` ->
 * ``'battery/GREENER_001/state'``. Flat, no leading slash (see ``topics.py``). Every real backend
 * roots its topics under an asset prefix (``battery``/``devices``/...) - the only room that is
 * ever unscoped is ``metadata``, handled separately via ``WatchTopicScheme.globalRoomTopics``.
 */
export const buildTopic = (assetPrefix: string, unitId: string, suffix: string): string =>
  `${assetPrefix}/${unitId}/${suffix}`;

/**
 * Full topic for a room. A ``globalRoomTopics`` entry bypasses scoping entirely (the literal
 * topic); otherwise the room's suffix is ``roomSuffixes[room] ?? room``, scoped under
 * ``assetPrefix``/``unitId`` as usual.
 */
export const buildRoomTopic = (
  unitId: string,
  room: WatchRoom,
  topicScheme: WatchTopicScheme = DEFAULT_WATCH_TOPIC_SCHEME
): string => {
  const globalTopic = topicScheme.globalRoomTopics?.[room];

  if (globalTopic !== undefined) {
    return globalTopic;
  }

  const suffix = topicScheme.roomSuffixes?.[room] ?? room;

  return buildTopic(topicScheme.assetPrefix, unitId, suffix);
};

const parseConfigAnnouncement = (payload: WatchPayload): WatchConfigAnnouncementPayload | null => {
  const { table, table_type: tableType, options } = payload;

  if (!isWatchConfigTable(table)) {
    return null;
  }

  if (tableType !== ROW_BASED_CONFIG && tableType !== COLUMN_BASED_CONFIG) {
    return null;
  }

  if (Array.isArray(options)) {
    return options.every(isPlainObject) ? { table, tableType, options } : null;
  }

  return isPlainObject(options) ? { table, tableType, options } : null;
};

/**
 * Unwraps the proxy's wire envelope (``{ payload, source }``, see
 * ``router.py::MqttRoomRouter.dispatch``) and returns null for anything that is not an accepted
 * room/payload combination.
 */
export const parseInboundMessage = (room: string, rawEnvelope: unknown): WatchInboundMessage | null => {
  if (!isWatchRoom(room)) {
    return null;
  }

  if (!isPlainObject(rawEnvelope) || !('payload' in rawEnvelope)) {
    return null;
  }

  const payload = (rawEnvelope as unknown as WatchEnvelope).payload;

  if (!isPlainObject(payload)) {
    return null;
  }

  if (room === 'feedback') {
    return typeof payload.command_uuid === 'string' ? { room, payload: payload as WatchFeedbackPayload } : null;
  }

  if (room === 'config/announcements') {
    const announcement = parseConfigAnnouncement(payload);

    return announcement ? { room, payload: announcement } : null;
  }

  if (isComponentDataRoom(room) && isPlainObject(payload.tags) && typeof payload.tags.component_id === 'string') {
    const componentId = payload.tags.component_id;
    const shouldKeepOnlyStreamIdentifier =
      room === 'components/input_data' && componentId !== WATCH_COMPONENT_IDS.MAIN_INPUT;

    return {
      room,
      payload: shouldKeepOnlyStreamIdentifier ? { stream_identifier: payload.stream_identifier } : payload,
      components: { [componentId]: payload },
    } as WatchInboundMessage;
  }

  return { room, payload } as WatchInboundMessage;
};

export const buildCommandMessage = ({
  unitId,
  command,
  requestId,
  args,
  topicScheme = DEFAULT_WATCH_TOPIC_SCHEME,
}: CommandMessageParams): WatchOutboundMessage => ({
  topic: buildTopic(topicScheme.assetPrefix, unitId, `commands/${command}`),
  payload: args === undefined ? { command_uuid: requestId } : { command_uuid: requestId, args },
  correlationKey: buildCommandCorrelationKey(requestId),
});

export const buildRequestConfigMessage = ({
  unitId,
  table,
  topicScheme = DEFAULT_WATCH_TOPIC_SCHEME,
}: ConfigMessageParams): WatchOutboundMessage => ({
  topic: buildTopic(topicScheme.assetPrefix, unitId, `${topicScheme.configCommandBase ?? 'config'}/request_config`),
  payload: { table },
  correlationKey: buildConfigCorrelationKey(table),
});

export const buildChangeConfigMessage = ({
  unitId,
  table,
  options,
  topicScheme = DEFAULT_WATCH_TOPIC_SCHEME,
}: ChangeConfigMessageParams): WatchOutboundMessage => {
  const recordId = options.id ?? options[getConfigTableIdColumn(table)];

  return {
    topic: buildTopic(topicScheme.assetPrefix, unitId, `${topicScheme.configCommandBase ?? 'config'}/change_config`),
    payload: { table, args: options },
    correlationKey: buildConfigCorrelationKey(
      table,
      typeof recordId === 'string' || typeof recordId === 'number' ? recordId : null
    ),
  };
};
